export interface ReorderAnchor {
  afterReportId: string | null;
  beforeReportId: string | null;
}

/**
 * Computes where a report that was dropped inside the current page should be placed
 * relative to its new neighbours.
 *
 * `orderedIds` is the page's report ids in their NEW order (after the move) and
 * `currentIndex` is the position the dragged report now occupies in that array.
 *
 * - When there is a row directly above, the report is anchored after it.
 * - When it landed first on the page, it is anchored before the row now directly below,
 *   so dropping at the top of a page that is not page 1 does not jump to the global top.
 * - Returns null when there is no neighbour to anchor to (single row) or the index is
 *   out of range; callers must treat that as a no-op.
 */
export function computeReorderAnchor(
  orderedIds: string[],
  currentIndex: number,
): ReorderAnchor | null {
  if (!Array.isArray(orderedIds) || orderedIds.length < 2) {
    return null;
  }
  if (!Number.isInteger(currentIndex) || currentIndex < 0 || currentIndex >= orderedIds.length) {
    return null;
  }

  const above = currentIndex > 0 ? orderedIds[currentIndex - 1] : null;
  if (above) {
    return { afterReportId: above, beforeReportId: null };
  }

  const below = orderedIds[currentIndex + 1] ?? null;
  if (below) {
    return { afterReportId: null, beforeReportId: below };
  }

  return null;
}
